import classNames from "classnames";
import { motion } from "framer-motion";
import { FC, ReactNode, RefObject, useId, useRef } from "react";

const NavItem: FC<{
  parentRef: RefObject<null>;
  children: ReactNode;
  onClick: () => void;
  className: string;
}> = ({ parentRef, children, onClick = () => {}, className }) => {
  const ref = useRef(null);

  return (
    <button
      type="button"
      ref={ref}
      onClick={() => {
        if (ref.current && parentRef.current) {
          const ref_ = ref.current as HTMLButtonElement;
          const parentRef_ = parentRef.current as HTMLDivElement;

          const relativeLeft =
            ref_.getBoundingClientRect().left -
            parentRef_.getBoundingClientRect().left +
            parentRef_.scrollLeft;

          parentRef_.scrollLeft = relativeLeft - 32;

          onClick();
        }
      }}
      className={className}
    >
      {children}
    </button>
  );
};

export const TabNav: FC<{
  navs: {
    title: string | ReactNode;
    to: string;
    onClick: (val: string) => void;
  }[];
  className?: string;
  tabClassName?: string;
  activeTab: string;
  variant: "nav" | "switcher";
  theme?: "plain";
}> = ({
  navs = [],
  className = "",
  tabClassName = "",
  activeTab,
  variant = "nav",
  theme = "plain",
}) => {
  const navRef = useRef(null);
  const switcherLayoutId = useId();

  return (
    <div
      className={classNames(
        `hide-scroll-bar flex flex-row gap-3 overflow-x-scroll md:gap-4`,
        `!scroll-smooth transition-all duration-1000`,
        `bg-grey-50`,
        {
          "border-neut-200 rounded-[8px] bg-grey-50 p-1":
            variant === "switcher" && theme === "plain",
          "!gap-0": variant === "switcher",
        },
        className,
      )}
      ref={navRef}
    >
      {navs
        .filter((item) => item !== undefined)
        .map(({ title, to, onClick }, i) => {
          const key = `TabNav-${i}-${title}`;
          const isActive = activeTab === to;

          return (
            <NavItem
              parentRef={navRef}
              key={key}
              className={classNames(
                `relative flex min-w-max cursor-pointer items-center gap-1 text-center text-sm font-bold transition-colors duration-300`,
                {
                  "flex-1": variant !== "nav",

                  "!text-primary":
                    isActive &&
                    (variant === "nav" ||
                      (variant === "switcher" && theme === "plain")),

                  "p-1": variant === "nav",

                  "justify-center rounded-[4px] bg-transparent px-4 py-[6px] text-center text-grey-800":
                    variant === "switcher" && theme === "plain",
                },
                `${tabClassName}`,
              )}
              onClick={() => {
                onClick(to);
              }}
            >
              {variant === "switcher" && theme === "plain" && isActive && (
                <motion.div
                  layoutId={`tabnav-switcher-bg-${switcherLayoutId}`}
                  className="absolute inset-0 rounded-[inherit] bg-white shadow-sm"
                  transition={{ type: "spring", stiffness: 500, damping: 40 }}
                />
              )}

              <span className="relative z-10 flex items-center gap-1">
                {title}
              </span>

              {variant === "nav" && (
                <div
                  className={`absolute bottom-0 left-0 right-full w-0 border-b-2 border-transparent transition-all duration-500 ${
                    isActive &&
                    "!right-full !w-full rounded-full !border-primary"
                  }`}
                />
              )}
            </NavItem>
          );
        })}
    </div>
  );
};
